import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { earlyAccessFeatureSchema } from "./early-access";
import { publicIdSchema } from "./public-id";
export const feedbackSchema = z.strictObject({
  message: z
    .string()
    .trim()
    .min(1)
    .max(10000)
    .refine(
      (value) => new TextEncoder().encode(value).length <= 30000,
      "Message exceeds 30000 UTF-8 bytes",
    ),
  feedbackType: z.enum(["general", "idea", "bug"]).optional(),
});
export type FeedbackInput = z.output<typeof feedbackSchema>;
export type FeedbackRequest = z.input<typeof feedbackSchema>;
export const feedbackContract = {
  submitFeedback: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Not Found" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Feedback unavailable" },
    })
    .input(
      z.object({ query: z.strictObject({}).default({}), body: feedbackSchema }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/feedback",
        inputStructure: "detailed",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(z.object({ delivered: z.literal(true) })),
};

// In-app feedback reports: stored in the database (and passed on to the team
// by email when it is configured). A report carries the visit's public id at
// most, never note or transcript text.
export const FEEDBACK_REPORT_TYPES = [
  "bug",
  "idea",
  "note_quality",
  "other",
] as const;
export const feedbackReportTypeSchema = z.enum(FEEDBACK_REPORT_TYPES);
export type FeedbackReportType = z.output<typeof feedbackReportTypeSchema>;
export const FEEDBACK_REPORT_MAX_LENGTH = 4000;
// Reports one account may send per rolling hour.
export const FEEDBACK_REPORT_HOURLY_LIMIT = 10;
export const feedbackReportSchema = z.strictObject({
  type: feedbackReportTypeSchema,
  message: z
    .string()
    .trim()
    .min(1)
    .max(FEEDBACK_REPORT_MAX_LENGTH)
    .refine(
      (value) =>
        new TextEncoder().encode(value).length <=
        FEEDBACK_REPORT_MAX_LENGTH * 3,
      `Message exceeds ${FEEDBACK_REPORT_MAX_LENGTH * 3} UTF-8 bytes`,
    ),
  visitPublicId: publicIdSchema.optional(),
  // Web build that sent the report (a release SHA or "dev").
  appVersion: z
    .string()
    .regex(/^[A-Za-z0-9._@+-]{1,64}$/)
    .optional(),
  // Sent from a Beta feature's "Send feedback on this beta".
  featureKey: earlyAccessFeatureSchema.optional(),
});
export type FeedbackReportInput = z.output<typeof feedbackReportSchema>;
export type FeedbackReportRequest = z.input<typeof feedbackReportSchema>;
export const feedbackReportResultSchema = z.object({
  received: z.literal(true),
});
export const feedbackReportContract = {
  submitFeedbackReport: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Visit not found" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      TOO_MANY_REQUESTS: { message: "Too many feedback reports" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Feedback unavailable" },
    })
    .input(
      z.object({
        query: z.strictObject({}).default({}),
        body: feedbackReportSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/feedback/reports",
        inputStructure: "detailed",
        successStatus: 201,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(feedbackReportResultSchema),
};
